<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const amendments = [
  { id: 'AMD-2024-003', project: 'Solar Park Beta', type: 'Scope Change', date: '2024-07-12', status: 'Approved', statusColor: 'green' },
]

const statusClass = (c: string) => ({ green: 'bg-green-100 text-green-800', yellow: 'bg-yellow-100 text-yellow-800', red: 'bg-red-100 text-red-800' })[c] ?? 'bg-gray-100 text-gray-600'
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-4xl mx-auto px-5">

        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">Project Amendment</span>
        </nav>

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">Project Amendment</h1>
            <p class="text-gray-600">Submit a formal amendment request if your project's scope, methodology, boundaries, or other registered details have changed. Amendments require government approval.</p>
          </div>
        </section>

        <form class="space-y-6">
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-6 flex items-center">
              <i class="fas fa-edit text-primary mr-3"></i>Amendment Details
            </h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Select Project *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select project</option>
                  <option>Solar Park Beta – Northern Highlands</option>
                  <option>Mangrove Restoration Initiative – Borneo</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Amendment Type *</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>Select amendment type</option>
                  <option>Methodology Change</option>
                  <option>Scope Change (expansion / reduction)</option>
                  <option>Boundary Adjustment</option>
                  <option>Proponent Change / Ownership Transfer</option>
                  <option>DOE Change</option>
                  <option>Contact / Administrative Details</option>
                  <option>Other</option>
                </select>
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Description of Change *</label>
                <textarea rows="4" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Describe what has changed and why the amendment is required."></textarea>
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Impact on Emission Reductions</label>
                <textarea rows="2" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Describe any impact on estimated emission reductions."></textarea>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-xl font-semibold text-accent mb-4 flex items-center">
              <i class="fas fa-upload text-primary mr-3"></i>Supporting Documents
            </h2>
            <div class="border border-gray-200 rounded-lg p-4">
              <div class="flex items-center justify-between mb-2">
                <h3 class="font-medium text-accent">Amendment Supporting Document *</h3>
                <span class="text-xs text-error">Required</span>
              </div>
              <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
                <i class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
                <p class="text-gray-600 mb-1">Drag and drop or click to upload</p>
                <p class="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
              </div>
            </div>
          </section>

          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-center justify-between">
              <button type="button" class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                <i class="fas fa-save mr-2"></i>Save Draft
              </button>
              <button type="submit" class="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90">
                <i class="fas fa-paper-plane mr-2"></i>Submit Amendment
              </button>
            </div>
          </section>
        </form>

        <!-- Amendment history -->
        <section class="mt-10">
          <h2 class="text-lg font-semibold text-accent mb-4">Amendment History</h2>
          <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-gray-700 text-xs uppercase tracking-wide">
                <tr>
                  <th class="px-5 py-3 text-left">Ref</th>
                  <th class="px-5 py-3 text-left">Project</th>
                  <th class="px-5 py-3 text-left">Type</th>
                  <th class="px-5 py-3 text-left">Date</th>
                  <th class="px-5 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="a in amendments" :key="a.id" class="hover:bg-gray-50">
                  <td class="px-5 py-3 text-xs text-gray-500">{{ a.id }}</td>
                  <td class="px-5 py-3 text-gray-700">{{ a.project }}</td>
                  <td class="px-5 py-3 text-gray-700">{{ a.type }}</td>
                  <td class="px-5 py-3 text-gray-700">{{ a.date }}</td>
                  <td class="px-5 py-3">
                    <span :class="['inline-flex px-2 py-0.5 rounded-full text-xs font-medium', statusClass(a.statusColor)]">{{ a.status }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
